/**
 * PropertyCard Component
 * Generic property card for displaying property/listing information
 * Works with Property type from entities
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Home, Bed, Bath, MapPin, Calendar } from 'lucide-react';
import { formatPrice, computeDownPayment, estimateMonthlyPayment } from '@/utils';
import type { AffordabilityPrefs } from '@/utils';
import { ShareListing } from '@/components/common/ShareListing';
import { useAuthOptional } from '@/app/providers';
import type { Property, User } from '@/types/entities';

// ===========================================
// Types
// ===========================================

export interface PropertyCardProps {
    /** Property data to display */
    property: Property;
    /** Show year built */
    showYearBuilt?: boolean;
    /** Show property type */
    showPropertyType?: boolean;
}

// ===========================================
// Helpers
// ===========================================

/**
 * The signed-in user's affordability preferences (flat fields on the
 * /auth/me payload), or null for anonymous visitors.
 */
function getAffordabilityPrefs(user: User | null): AffordabilityPrefs | null {
    if (!user) return null;
    return {
        down_payment_mode: user.down_payment_mode ?? null,
        preferred_down_percentage: user.preferred_down_percentage ?? null,
        max_down_amount: user.max_down_amount ?? null,
    };
}

// ===========================================
// Component
// ===========================================

export function PropertyCard({
    property,
    showYearBuilt = true,
    showPropertyType = true,
}: PropertyCardProps) {
    const {
        listing_id,
        title,
        display_price,
        list_price,
        status,
        location,
        full_address,
        bedrooms,
        bathrooms,
        actual_sqft,
        square_feet,
        property_type,
        year_built,
        images,
    } = property;

    // useAuthOptional so unit tests (and any future surface that renders
    // a PropertyCard without an AuthProvider above) get null instead of
    // a hard throw. Anonymous visitors fall through to the 20%-down
    // default in computeDownPayment.
    const auth = useAuthOptional();
    const user = auth?.user ?? null;
    const navigate = useNavigate();
    const imageUrl = images?.main_image?.small_url;
    // Tracks whether the network image 404'd or otherwise failed to
    // load — when true we render the "Image Coming Soon" placeholder
    // exactly like the no-imageUrl path, instead of a broken-image
    // icon.
    const [imageFailed, setImageFailed] = useState(false);
    const showImage = Boolean(imageUrl) && !imageFailed;
    const priceDisplay = display_price || (list_price ? formatPrice(list_price) : 'Price N/A');
    const locationDisplay = location?.city && location?.state
        ? `${location.city}, ${location.state}`
        : full_address || 'Location N/A';
    const sqftDisplay = actual_sqft || (square_feet ? parseInt(square_feet) : 0);

    // Per-card monthly estimate. Uses the signed-in user's affordability
    // preferences when set; otherwise falls back to 20% down / 30yr /
    // calculator default rate.
    const numericPrice = typeof list_price === 'number' && list_price > 0 ? list_price : 0;
    const downPayment = computeDownPayment(numericPrice, getAffordabilityPrefs(user));
    const monthly = estimateMonthlyPayment(numericPrice, downPayment);
    const monthlyHref = numericPrice > 0
        ? `/mortgage-calculator?listing_id=${encodeURIComponent(listing_id)}&price=${numericPrice}`
        : null;

    return (
        <Link to={`/property/${listing_id}`} className="block h-full">
            <div className="h-full bg-white rounded-lg shadow-sm hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                {/* Property Image — falls back to the same "Image
                    Coming Soon" placeholder when the URL is missing OR
                    when the network image fails to load (e.g. a stale
                    Zillow CDN URL 404s). Without the onError fallback
                    visitors saw a broken-image icon instead. */}
                <div className="relative w-full h-64 bg-gray-200">
                    {showImage ? (
                        <img
                            src={imageUrl}
                            alt={title || 'Property'}
                            className="w-full h-full object-cover"
                            onError={() => setImageFailed(true)}
                        />
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full p-8">
                            <img
                                src="/Images/logo-transparent.gif"
                                alt="Property Placeholder"
                                className="w-24 h-24 object-contain opacity-50"
                            />
                            <p className="text-gray-500 mt-4 text-sm">Image Coming Soon</p>
                        </div>
                    )}

                    {/* Status Badge */}
                    {status && (
                        <div className="absolute top-3 left-3">
                            <span className="px-2 py-1 bg-primary text-white text-sm font-semibold rounded">
                                {typeof status === 'string' ? status.replace('_', ' ') : status}
                            </span>
                        </div>
                    )}

                    {/* ShareListing stops its own clicks from reaching
                        the <Link> that wraps this whole card. */}
                    <ShareListing
                        variant="icon"
                        className="absolute top-3 right-3"
                        listingId={String(listing_id)}
                        address={title || full_address}
                        locality={locationDisplay}
                        price={priceDisplay}
                        beds={bedrooms != null ? String(bedrooms) : undefined}
                        baths={bathrooms != null ? String(bathrooms) : undefined}
                        sqft={sqftDisplay ? String(sqftDisplay) : undefined}
                    />
                </div>

                {/* Property Info */}
                <div className="p-4">
                    {/* Price */}
                    <h3 className="text-xl font-bold text-primary mb-1">
                        {priceDisplay}
                    </h3>

                    {/* Per-month estimate — navigates to the calculator
                        with this listing pre-filled. Rendered as a button
                        (not a Link) because the entire card is already a
                        Link to the property detail; nesting Links is
                        invalid HTML. preventDefault + stopPropagation
                        prevent the outer card-click from hijacking us. */}
                    {monthly > 0 && monthlyHref && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(monthlyHref);
                            }}
                            className="inline-block text-xs text-gray-500 hover:text-primary mb-2"
                        >
                            est. {formatPrice(Math.round(monthly))}/mo →
                        </button>
                    )}

                    {/* Title */}
                    <h4 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                        {title || location?.address || 'Property'}
                    </h4>

                    {/* Location */}
                    <div className="flex items-start gap-1 text-gray-600 mb-4">
                        <MapPin size={16} className="mt-1 flex-shrink-0" />
                        <p className="text-sm line-clamp-2">{locationDisplay}</p>
                    </div>

                    {/* Property Details */}
                    <div className="flex items-center gap-4 text-sm text-gray-600 border-t pt-3">
                        {bedrooms !== undefined && bedrooms > 0 && (
                            <div className="flex items-center gap-1">
                                <Bed size={16} />
                                <span>{bedrooms} bd</span>
                            </div>
                        )}
                        {bathrooms !== undefined && bathrooms > 0 && (
                            <div className="flex items-center gap-1">
                                <Bath size={16} />
                                <span>{bathrooms} ba</span>
                            </div>
                        )}
                        {sqftDisplay > 0 && (
                            <div className="flex items-center gap-1">
                                <Home size={16} />
                                <span>{sqftDisplay.toLocaleString()} sqft</span>
                            </div>
                        )}
                    </div>

                    {/* Property Type & Year */}
                    {(showPropertyType || showYearBuilt) && (
                        <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                            {showPropertyType && property_type && (
                                <span>{property_type}</span>
                            )}
                            {showYearBuilt && year_built && year_built > 0 && (
                                <div className="flex items-center gap-1">
                                    <Calendar size={12} />
                                    <span>Built {year_built}</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}

export default PropertyCard;
